/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on May 22, 2026 by hoarfroster
 *
 */

import { ReactNode, useEffect, useState } from 'react';
import { getStorageItem } from '@utils/storage.ts';
import { PreferencesContext, Preferences } from '../contexts/preferences-context';

// Define a default preference set
const defaultPreferences: Preferences = {
    preloadMedia: false,
    requireAuthentication: true,
    enableDeviceAuthentication: false,
    hideFooter: false,
    backgroundImagePath: '',
    serverEndpoint: import.meta.env.VITE_SERVER_ENDPOINT ?? '',
    locale: 'zh-CN', // Set a default locale that matches the available locales
};

// Provider component
export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
    const [preferences, setPreferences] = useState<Preferences>(defaultPreferences);

    // Load preferences from local storage when the component mounts
    useEffect(() => {
        const savedPreferences = getStorageItem('user-preferences');
        if (savedPreferences) {
            setPreferences(JSON.parse(savedPreferences));
        }
    }, []);

    // Update preferences and save them to local storage
    const updatePreferences = (newPreferences: Partial<Preferences>) => {
        const updatedPreferences = { ...preferences, ...newPreferences };
        setPreferences(updatedPreferences);
        localStorage.setItem('user-preferences', JSON.stringify(updatedPreferences));
    };

    return (
        <PreferencesContext.Provider value={{ preferences, updatePreferences }}>{children}</PreferencesContext.Provider>
    );
};
